import { Directive, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { CountriesService } from '@zero/onboarding/domain';
import { AtAlgorithm } from './at-algorithm';
import { ChAlgorithm } from './ch-algorithm';
import { DeAlgorithm } from './de-algorithm';

/**
 * https://polzer.wordpress.com/2011/10/06/ein-validator-der-steuer-identifikationsnummer-in-java/
 *
 */

@Directive({
  selector: '[formValidatorsTinValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: TinValidatorDirective, multi: true }],
  standalone: true,
})
export class TinValidatorDirective implements Validator, OnChanges {
  @Input() public countryCode: string; // ISO country code
  @Input() public tinValidatorForbiddenTin: string;
  private onChange: () => void;
  private deTinValidator = new DeAlgorithm();
  private atTinValidator = new AtAlgorithm();
  private chTinValidator = new ChAlgorithm();

  constructor(private countriesService: CountriesService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('countryCode' in changes) {
      if (this.onChange) {
        this.onChange();
      }
    }
  }

  /**
   * Verwendung von registerOnValidatorChange:
   * https://medium.com/front-end-weekly/angular-how-to-implement-conditional-custom-validation-1ec14b0feb45
   * (die Callback-Fn kann man aufrufen, um eine erneute Validierung auszulösen. Wir machen das hier,
   * wenn sich der Wert der Input-Value der Directive, also "validateForCountry" ändert.)
   */
  public registerOnValidatorChange(fn: () => void): void {
    this.onChange = fn;
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    if (!this.countryCode || this.countryCode === '') {
      return null;
    }

    if (this.countryCode === 'DE' || this.countryCode === 'AT' || this.countryCode === 'CH') {
      const validationResult = this.validateCountryTin(control.value);
      if (validationResult !== null) {
        return { invalidCountryTin: validationResult };
      }
    } else {
      if (this.countriesService.isoCodesEU.indexOf(this.countryCode) > -1) {
        // zwischen 8 u. 16 Zeichen, Buchstaben, Ziffern, Schrägstrich, Bindestrich
        // eslint-disable-next-line no-useless-escape
        const pat = /[A-Za-z0-9\-\/]{8,16}/;
        if (!(control.value || '').trim().match(pat)) {
          return { invalidLength: true };
        }
      }

      if (!(control.value && control.value.length > 0)) {
        return { invalidLength: true };
      }
    }

    if (this.tinValidatorForbiddenTin) {
      const enteredTin = (control.value || '').toLowerCase().replace(/ /g, '');
      const forbiddenTin = (this.tinValidatorForbiddenTin || '').toLowerCase().replace(/ /g, '');
      if (enteredTin === forbiddenTin && forbiddenTin !== '') {
        return { forbiddenTin: true };
      }
    }

    return null;
  }

  public validateCountryTin(tin: string): string | null {
    if (!tin) {
      return null;
    }
    if (this.countryCode === 'DE') {
      return this.deTinValidator.validate(tin);
    }
    if (this.countryCode === 'AT') {
      return this.atTinValidator.validate(tin);
    }
    if (this.countryCode === 'CH') {
      return this.chTinValidator.validate(tin);
    }
    return null;
  }
}
